import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Form, Formik } from 'formik'
import { H, Wrapper } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'
import * as yup from 'yup'

import axiosRequest from 'lib/axios/api-request'
import StyledLoader from 'components/loader'
import { VoucherDiscount, type VoucherAttributes } from 'lib/models/voucher'
import VoucherForm from '../components/voucher-form'
import { type VoucherFormValues } from '../interfaces'

const usageLimitField = (currentUsage: number) =>
  yup
    .number()
    .integer()
    .min(1)
    .nullable()
    .test(
      'gt-usage',
      `Must be greater than current usage (${currentUsage})`,
      (val) => val == null || val > currentUsage
    )

const offerAttributesSchema = yup.object({
  partnerName: yup.string().required('Partner name is required for gift cards'),
  offer: yup.string().required('Offer description is required for gift cards'),
})

const usedGiftCardOfferAttributesSchema = yup.object({
  offer: yup.string().required('Offer description is required for gift cards'),
})

const buildSchema = (
  isUsed: boolean,
  currentUsage: number,
  isGiftCard: boolean
) => {
  const base = yup.object({
    expiresAt: yup.string().required('Expiry date is required'),
    usageLimit: usageLimitField(currentUsage),
  })

  if (isUsed) {
    return isGiftCard
      ? base.concat(
          yup.object({ offerAttributes: usedGiftCardOfferAttributesSchema })
        )
      : base
  }

  return base.concat(
    yup.object({
      productName: yup.string().required('Product is required'),
      discountType: yup.string().required('Discount type is required'),
      discountValue: yup.number().when('discountType', {
        is: (val: string) => val !== VoucherDiscount.giftCard,
        then: (schema) => schema.min(0).required('Discount value is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      code: yup.string().required('Code is required'),
      brandId: yup.string().required('Brand is required'),
      mechanism: yup.string().nullable(),
      offerAttributes: yup.object().when('discountType', {
        is: VoucherDiscount.giftCard,
        then: () => offerAttributesSchema,
        otherwise: (schema) =>
          schema.shape({
            partnerName: yup.string().notRequired(),
            offer: yup.string().notRequired(),
          }),
      }),
    })
  )
}

const attributesToFormValues = (
  attrs: VoucherAttributes
): VoucherFormValues => ({
  productName: attrs.productName,
  discountType: attrs.discountType,
  discountValue: attrs.discountValue,
  code: attrs.code,
  brandId: attrs.brandId ?? '',
  expiresAt: attrs.expiresAt ?? '',
  usageLimit: attrs.usageLimit ?? '',
  mechanism: attrs.mechanism ?? '',
  offerAttributes: {
    partnerName: (attrs.offerAttributes?.partnerName as string) ?? '',
    offer: (attrs.offerAttributes?.offer as string) ?? '',
  },
})

const VoucherDetail = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()

  const [isLoading, setIsLoading] = useState(true)
  const [isUsed, setIsUsed] = useState(false)
  const [currentUsage, setCurrentUsage] = useState(0)
  const [formValues, setFormValues] = useState<VoucherFormValues | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      const { data } = await axiosRequest({ url: `/api/vouchers/${id}` })
      const attrs: VoucherAttributes = data.data.attributes
      setIsUsed(attrs.referrals > 0)
      setCurrentUsage(attrs.usage)
      setFormValues(attributesToFormValues(attrs))
      setIsLoading(false)
    }
    load()
  }, [id])

  const isGiftCard = formValues?.discountType === VoucherDiscount.giftCard

  const validationSchema = useMemo(
    () => buildSchema(isUsed, currentUsage, isGiftCard),
    [isUsed, currentUsage, isGiftCard]
  )

  const handleSubmit = async (values: VoucherFormValues) => {
    setSubmitError(null)
    try {
      const usageLimit =
        values.usageLimit !== '' ? Number(values.usageLimit) : null

      const giftCardOfferAttributes =
        values.discountType === VoucherDiscount.giftCard
          ? {
              partnerName: values.offerAttributes.partnerName,
              offer: values.offerAttributes.offer,
            }
          : null

      const attributes = isUsed
        ? {
            expiresAt: values.expiresAt || null,
            usageLimit,
            ...(isGiftCard &&
              giftCardOfferAttributes && {
                offerAttributes: {
                  partnerName: values.offerAttributes.partnerName,
                  offer: giftCardOfferAttributes.offer,
                },
              }),
          }
        : {
            expiresAt: values.expiresAt || null,
            usageLimit,
            offerAttributes: giftCardOfferAttributes,
          }

      await axiosRequest({
        url: `/api/vouchers/${id}`,
        method: 'PATCH',
        data: {
          data: { id, type: 'vouchers', attributes },
        },
      })

      history.push('/admin/vouchers')
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { errors?: { title?: string }[] } } })
          ?.response?.data?.errors?.[0]?.title ?? 'Failed to update voucher'
      setSubmitError(message)
    }
  }

  if (isLoading || !formValues) return <StyledLoader />

  return (
    <Wrapper container>
      <H tag="h1" size="L" margin={[GTR.L, 0, 0]}>
        Edit voucher code
      </H>
      {submitError && (
        <Wrapper
          margin={[GTR.M, 0, 0]}
          padding={GTR.M}
          style={{
            background: '#f8d7da',
            borderRadius: 4,
            border: '1px solid #f5c2c7',
          }}
        >
          {submitError}
        </Wrapper>
      )}
      <Formik
        initialValues={formValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <VoucherForm
              isUsed={isUsed}
              backPath="/admin/vouchers"
              isSubmitting={isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </Wrapper>
  )
}

export default VoucherDetail
